import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { meQuery } from "@/lib/queries";
import { useLogout } from "@/lib/mutations";
import { Button, buttonStyles } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.user) {
      throw redirect({ to: "/login" });
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
});

function emailHandle(email: string | null | undefined): string {
  if (!email) return "stranger";
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function ProfilePage() {
  const router = useRouter();
  const { data } = useQuery(meQuery);
  const logout = useLogout();

  if (!data || !data.user) {
    // beforeLoad guards this, but render-time TS narrowing wants it.
    return null;
  }

  const handle = emailHandle(data.user.email);

  async function onLogout() {
    if (logout.isPending) return;
    await logout.mutateAsync();
    await router.navigate({ to: "/login" });
  }

  return (
    <section className="mx-auto mt-10 max-w-xl space-y-6">
      <header className="px-1">
        <h1 className="font-display text-4xl text-locomotive-900">
          Howdy, {handle}
        </h1>
        <p className="font-mono text-xs text-muted-foreground">
          Signed in across this device.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="font-mono text-sm">
            <div className="flex items-baseline justify-between gap-3 border-b border-border/50 py-2">
              <dt className="text-muted-foreground">email</dt>
              <dd className="text-foreground">
                {data.user.email ?? <em>none</em>}
              </dd>
            </div>
          </dl>
        </CardContent>
        <div className="mt-4 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            disabled={logout.isPending}
          >
            {logout.isPending ? "Logging out…" : "Log out"}
          </Button>
        </div>
      </Card>

      <section>
        <h2 className="font-display text-2xl mb-3">Your games</h2>
        {data.players.length === 0 ? (
          <Card>
            <CardContent>
              <p className="font-mono text-sm text-muted-foreground">
                No active games. Start one.
              </p>
              <div className="mt-4">
                <Link
                  to="/games/create"
                  className={buttonStyles("primary", "lg")}
                >
                  New game
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3">
            {data.players.map((p) => (
              <li key={p.id}>
                <Link
                  to="/games/$id/lobby"
                  params={{ id: p.gameId }}
                  className="block"
                >
                  <Card className="transition-colors hover:border-rust-500/60">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display text-xl">
                          {p.displayName}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          game {p.gameId}
                        </div>
                      </div>
                      <span className="font-mono text-sm text-rust-600">
                        Continue →
                      </span>
                    </div>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
